import { useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { QuoteWizardState, StoreState } from '../store';
import { FINAL_FORM } from '../queries';
import { useLazyQuery } from '@apollo/client';
import { Language } from '../Typescript/classes';
import { ProgramBuilderForm } from '../components/FormManager/classes';
import { QuoteWizardForm } from '../components/QuoteWizard/classes';

type UseEnsureCalefyFormPresentParams = null | {
  internalName?: string;
};

type UseEnsureCalefyFormPresentResult = {
  form: QuoteWizardForm | null | undefined;
  loading: boolean;
};

/**
 * Ensure that a form is present in the quote wizard store by internal name,
 * fetching from the backend when needed.
 */
export const useEnsureCalefyFormPresent = (
  params: UseEnsureCalefyFormPresentParams = {},
): UseEnsureCalefyFormPresentResult => {
  const [internalName, setInternalName] = useState<string | null>(null);
  const getForm = useCallback(
    (store: StoreState) => {
      if (!internalName) {
        return undefined;
      }
      return store.quoteWizard.policyForms.find(
        (form) => form.internalName === internalName,
      );
    },
    [internalName],
  );
  const formInStore = useSelector(getForm);
  const dispatch = useDispatch();
  const [getFinalForm, { loading, data }] = useLazyQuery(FINAL_FORM);

  useEffect(() => {
    setInternalName(params?.internalName ?? null);
  }, [params?.internalName]);

  useEffect(() => {
    if (!loading && !formInStore && internalName) {
      getFinalForm({
        variables: {
          internalName,
        },
      });
    }
  }, [internalName, formInStore, loading, getFinalForm]);

  useEffect(() => {
    if (!internalName || !data?.finalForm) {
      return;
    }
    const responseInternalName = data.finalForm.internalName;
    if (responseInternalName && responseInternalName !== internalName) {
      return;
    }
    if (!formInStore) {
      const newForm = ProgramBuilderForm.generateFromBackendResponse(
        data.finalForm,
      ).toQuoteWizardForm(Language.english);
      dispatch(QuoteWizardState.actions.addForms({ forms: newForm }));
    }
  }, [data, formInStore, internalName, dispatch]);

  return {
    form: formInStore,
    loading: Boolean(internalName) && loading,
  };
};
